import React, { useState, useRef } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { useButtonState } from '../../hooks/useButtonState';
import { downloadCsv } from '../../utils/csvUtils';
import { extractApiError } from '../../services/api';
import '../../styles/components/UploadModal.css';

export interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File, updateExisting: boolean) => Promise<{
    message?: string;
    data: {
      inserted_count?: number;
      updated_count?: number;
      skipped_count?: number;
      failure_count?: number;
      errors?: Array<{ row: number; data: any; error: string }>;
    };
  }>;
  onUploadSuccess?: () => void;
  filenameBase?: string;
  instructions?: React.ReactNode;
  acceptedFileTypes?: string[];
  modalTitle?: string;
  fileLabel?: string;
  fileTypeDescription?: string;
  filenamePattern?: RegExp;
  filenamePatternHelpText?: string;
}

const UploadModal: React.FC<UploadModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  onUploadSuccess,
  filenameBase = 'searchlist',
  instructions,
  acceptedFileTypes = ['.csv'],
  modalTitle = 'Upload File',
  fileLabel,
  fileTypeDescription,
  filenamePattern,
  filenamePatternHelpText,
}) => {
  const { showToast } = useToast();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);
  const [uploadErrors, setUploadErrors] = useState<any[]>([]);
  const [filenameError, setFilenameError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadErrorsRef = useRef<HTMLDivElement>(null);
  const uploadButtonState = useButtonState('Upload', 'Uploading...', 'Uploaded ✅');

  const normalizedExtensions = (acceptedFileTypes.length ? acceptedFileTypes : ['.csv'])
    .map(ext => (ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`));
  const acceptAttr = normalizedExtensions.join(',');
  const formattedExtensions = normalizedExtensions
    .map(ext => ext.replace('.', '').toUpperCase())
    .join(', ');
  const fileLabelText = fileLabel || `Select File (${formattedExtensions})`;
  const fileHintText = fileTypeDescription || `Allowed formats: ${formattedExtensions}`;

  // Validate file
  const validateAndSetFile = (file: File) => {
    const dotIndex = file.name.lastIndexOf('.');
    const extension = dotIndex >= 0 ? file.name.slice(dotIndex).toLowerCase() : '';
    if (!extension || !normalizedExtensions.includes(extension)) {
      const message = `Please select a file with one of the following extensions: ${formattedExtensions}`;
      showToast(message, 'error');
      setFilenameError(message);
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return false;
    }

    if (filenamePattern && !filenamePattern.test(file.name)) {
      const message = filenamePatternHelpText || 'File name does not match the required format.';
      showToast(message, 'error');
      setFilenameError(message);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFile(null);
      return false;
    }

    setSelectedFile(file);
    setUploadResult(null);
    setUploadErrors([]);
    setFilenameError(null);
    return true;
  };

  // Handle file selection
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    } else {
      setSelectedFile(null);
      setFilenameError(null);
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  // Scroll to error section - shared function for both auto-scroll and manual scroll
  // Uses dynamic calculation based on actual element positions (not device-dependent)
  const scrollToErrors = () => {
    if (!uploadErrorsRef.current) return;
    
    const modalBody = uploadErrorsRef.current.closest('.upload-modal-body');
    if (!modalBody) return;

    const errorSectionTop = uploadErrorsRef.current.offsetTop;
    // Calculate offset based on summary height to keep it visible
    // This approach is responsive and works across different screen sizes
    const summaryHeight = 280; // Approximate height of summary box
    const padding = 60; // Additional padding for better visibility
    const targetScroll = errorSectionTop - summaryHeight - padding;
    
    modalBody.scrollTo({
      top: Math.max(0, targetScroll),
      behavior: 'smooth'
    });
  };

  // Handle upload
  const handleUpload = async () => {
    // Read file fresh from input to get latest file data (in case file was updated on disk)
    const fileInput = fileInputRef.current;
    if (!fileInput || !fileInput.files || fileInput.files.length === 0) {
      if (!selectedFile) {
        showToast('Please select a file to upload', 'error');
        return;
      }
      // If input has no file but we have selectedFile in state, use it
      // This handles the case where file input might have been cleared
    }

    // Always read the file fresh from the input to get the latest version
    const fileToUpload = fileInput?.files?.[0] || selectedFile;
    if (!fileToUpload) {
      showToast('Please select a file to upload', 'error');
      return;
    }

    uploadButtonState.startLoading();
    setUploadResult(null);
    setUploadErrors([]);
    setFilenameError(null);

    try {
      // Call parent's upload function
      const result = await onUpload(fileToUpload, updateExisting);
      
      // Store filename before clearing for display in summary
      const uploadedFileName = fileToUpload?.name || selectedFile?.name || 'Unknown file';
      
      // Update result with filename for display
      setUploadResult({
        ...result,
        uploadedFileName
      });

      // Show success toast with summary
      const message = result.message || 'Upload completed';
      const failureCount = result.data.failure_count || 0;
      const summary = `Inserted: ${result.data.inserted_count || 0}, Updated: ${result.data.updated_count || 0}, Skipped: ${result.data.skipped_count || 0}, Failed: ${failureCount}`;
      showToast(`${message} - ${summary}`, failureCount > 0 ? 'warning' : 'success', 5000);

      // If there are errors, show them
      if (result.data.errors && result.data.errors.length > 0) {
        setUploadErrors(result.data.errors);
        // Scroll to error section after a brief delay to ensure it's rendered
        setTimeout(() => {
          scrollToErrors();
        }, 100);
      } else {
        setUploadErrors([]);
      }

      // Clear file input and selectedFile state after upload (but keep results visible)
      // This ensures user must re-select file for next upload, preventing stale file issues
      // Also, if there are errors, user will need to fix the file and re-select anyway
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setSelectedFile(null);

      uploadButtonState.showSuccess(2000);
      setFilenameError(null);
    } catch (error: unknown) {
      console.error('Upload error:', error);
      const extracted = await extractApiError(error);
      showToast(extracted.message, 'error');

      const fileErrors =
        extracted.fieldErrors?.file ||
        extracted.fieldErrors?.filename ||
        extracted.fieldErrors?.name;

      if (Array.isArray(fileErrors) && fileErrors.length > 0) {
        setFilenameError(fileErrors[0]);
      } else {
        setFilenameError(null);
      }
      uploadButtonState.reset();
    }
  };

  // Handle download error rows as CSV
  const handleDownloadErrorRows = () => {
    if (uploadErrors.length === 0) return;

    // Extract all unique keys from error rows
    const allKeys = new Set<string>();
    uploadErrors.forEach(error => {
      if (error.data) {
        Object.keys(error.data).forEach(key => allKeys.add(key));
      }
    });

    // Build CSV rows with row number, error message, and all data fields
    const headerRow = ['row', 'error', ...Array.from(allKeys)];
    const dataRows = uploadErrors.map(err => [
      String(err.row || ''),
      String(err.error || ''),
      ...Array.from(allKeys).map(key => String(err.data?.[key] || ''))
    ]);

    // Use downloadCsv directly for error rows (different structure than normal export)
    downloadCsv([headerRow, ...dataRows], `${filenameBase}-upload-errors`);
  };

  // Handle close
  const handleClose = () => {
    // Refresh parent data before closing (user can't see changes while modal is open anyway)
    if (uploadResult) {
      onUploadSuccess?.();
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setSelectedFile(null);
    setUpdateExisting(false);
    setUploadResult(null);
    setUploadErrors([]);
    setFilenameError(null);
    uploadButtonState.reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="upload-modal-overlay" onClick={() => !uploadButtonState.isLoading && handleClose()}>
      <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="upload-modal-header">
          <h2>{modalTitle}</h2>
          <button 
            className="upload-modal-close"
            onClick={handleClose}
            disabled={uploadButtonState.isLoading}
          >
            ×
          </button>
        </div>

        <div className="upload-modal-body">
          <div className="upload-form-group">
            <label htmlFor="metadata-file" className="upload-field-label">{fileLabelText}</label>
            <div 
              className={`upload-file-zone ${isDragOver ? 'drag-over' : ''} ${selectedFile ? 'has-file' : ''} ${filenameError ? 'has-error' : ''}`}
              style={filenameError ? { borderColor: '#e53935' } : undefined}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                id="metadata-file"
                accept={acceptAttr}
                onChange={handleFileSelect}
                disabled={uploadButtonState.isLoading}
                className="upload-file-input-hidden"
              />
              {selectedFile ? (
                <div className="upload-file-selected">
                  <span className="material-symbols-outlined upload-file-icon-large">description</span>
                  <div className="upload-file-details">
                    <span className="upload-file-name">{selectedFile.name}</span>
                    <span className="upload-file-size">{(selectedFile.size / 1024).toFixed(2)} KB</span>
                  </div>
                  <button
                    type="button"
                    className="upload-file-change-button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadButtonState.isLoading}
                    title="Change file"
                  >
                    <span className="material-symbols-outlined">edit</span>
                  </button>
                </div>
              ) : (
                <label htmlFor="metadata-file" className="upload-file-button">
                  <span className="material-symbols-outlined upload-button-icon">upload_file</span>
                  <span className="upload-button-text">Choose File</span>
                  <span className="upload-button-hint">or drag and drop</span>
                </label>
              )}
            </div>
            {filenameError && (
              <p className="upload-field-error" style={{ color: '#e53935' }}>
                {filenameError}
              </p>
            )}
          </div>

          <div className="upload-form-group">
            <label className="upload-checkbox-label">
              <input
                type="checkbox"
                checked={updateExisting}
                onChange={(e) => setUpdateExisting(e.target.checked)}
                disabled={uploadButtonState.isLoading}
                className="upload-checkbox"
              />
              <span className="upload-checkbox-text">Update existing records</span>
            </label>
          </div>

          {!filenameError && (
            <p className="upload-field-hint">
              {fileHintText}
            </p>
          )}
          {instructions && (
            <div className="upload-instructions">
              {instructions}
            </div>
          )}

          {uploadResult && (
            <div className="upload-result-summary">
              <div className="upload-result-header">
                <button
                  type="button"
                  className="upload-result-filename-link"
                  onClick={() => {
                    if (fileInputRef.current && !uploadButtonState.isLoading) {
                      fileInputRef.current.click();
                    }
                  }}
                  disabled={uploadButtonState.isLoading}
                  title="Click to select file again"
                >
                  {uploadResult.uploadedFileName || 'File'}
                </button>
                <span className="upload-result-title"> upload summary</span>
              </div>
              <div className="upload-result-content">
                <div className="upload-result-message">{uploadResult.message || 'Upload completed'}</div>
                <div className="upload-result-stats">
                  <div className="stat-item stat-inserted">
                    <span className="stat-label">Inserted:</span>
                    <span className="stat-value">{uploadResult.data.inserted_count || 0}</span>
                  </div>
                  <div className="stat-item stat-updated">
                    <span className="stat-label">Updated:</span>
                    <span className="stat-value">{uploadResult.data.updated_count || 0}</span>
                  </div>
                  <div className="stat-item stat-skipped">
                    <span className="stat-label">Skipped:</span>
                    <span className="stat-value">{uploadResult.data.skipped_count || 0}</span>
                  </div>
                  <button
                    type="button"
                    className={`stat-item stat-failed ${uploadResult.data.failure_count > 0 ? 'stat-failed-clickable' : ''}`}
                    onClick={() => {
                      if (uploadResult.data.failure_count > 0) {
                        scrollToErrors();
                      }
                    }}
                    disabled={uploadResult.data.failure_count === 0}
                    title={uploadResult.data.failure_count > 0 ? 'Click to view errors below' : ''}
                  >
                    <span className="stat-label">Failed:</span>
                    <span className="stat-value">{uploadResult.data.failure_count || 0}</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {uploadErrors.length > 0 && (
            <div className="upload-errors-section" ref={uploadErrorsRef}>
              <div className="upload-errors-header">
                <h3>Errors ({uploadErrors.length})</h3>
                <button 
                  className="upload-download-errors-button"
                  onClick={handleDownloadErrorRows}
                  title="Download error rows as CSV"
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px', marginRight: '4px' }}>download</span>
                  Download Errors
                </button>
              </div>
              <div className="upload-errors-table-container">
                <table className="upload-errors-table">
                  <thead>
                    <tr>
                      <th>Row</th>
                      <th>Error</th>
                      {uploadErrors.length > 0 && uploadErrors[0].data && Object.keys(uploadErrors[0].data).map(key => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {uploadErrors.map((error, index) => (
                      <tr key={index}>
                        <td className="error-row-number">{error.row}</td>
                        <td className="error-message">{error.error}</td>
                        {error.data && Object.entries(error.data).map(([key, value]) => (
                          <td key={key}>{String(value)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="upload-modal-footer">
          <button
            className="upload-modal-cancel-button"
            onClick={handleClose}
            disabled={uploadButtonState.isLoading}
            title="Close and clear all upload data"
          >
            {uploadResult?.data ? 'Close' : 'Cancel'}
          </button>
          <button
            className="upload-modal-submit-button"
            onClick={handleUpload}
            disabled={!selectedFile || !!filenameError || uploadButtonState.isLoading}
          >
            {uploadButtonState.buttonText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;


